import Webcam from "react-webcam";
import { useGeolocated } from "react-geolocated";
import piexif from "piexifjs";
import { isMobile } from "react-device-detect";

import { useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Grid,
  Icon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
  Box,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import MDButton from "theme/components/MDButton";
import AppConfig from "appConfig";

function GeoTaggedImageField({ displayName, fieldRef, options }) {
  const [fieldError, showFieldError] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const webcamRef = useRef(`${fieldRef}_${displayName}`);
  const [imgSrc, setImgSrc] = useState(null);
  const [capturedData, setCapturedData] = useState(null);
  const [cameraUsed, setCameraUsed] = useState(false);

  const videoConstraints = {
    facingMode: cameraUsed ? "user" : { exact: "environment" },
  };

  const [openWebCamDialog, setOpenWebCamDialog] = useState(false);

  async function getGeocodeData(lat, lng) {
    const apiKey = AppConfig.google_map_key;
    const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`;

    const response = await fetch(url);
    if (!response.ok) return null;

    const data = await response.json();
    return data.results.length > 0 ? data.results[0].formatted_address : null;
  }

  function getHaversineDistance(lat1, lon1, lat2, lon2) {
    const R = 6371e3;
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) ** 2 +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;

    return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
  }

  async function addWatermark(base64, text) {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = base64;
    });

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    canvas.width = img.width;
    canvas.height = img.height;

    ctx.drawImage(img, 0, 0);

    /* SMALLER FONT */
    const fontSize = Math.round(img.width * 0.032); // ~2.5% of width
    const lineHeight = fontSize * 1.25;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.font = `${fontSize}px Arial`;
    ctx.textBaseline = "top";

    /* Limit watermark width */
    const maxWidth = img.width * 0.5;

    const paragraphs = text.split(/\r?\n/);
    const wrappedLines = [];

    for (const para of paragraphs) {
      const words = para.split(" ");
      let line = "";

      for (const word of words) {
        const testLine = line ? `${line} ${word}` : word;
        const width = ctx.measureText(testLine).width;

        if (width > maxWidth) {
          wrappedLines.push(line);
          line = word;
        } else {
          line = testLine;
        }
      }

      if (line) wrappedLines.push(line);
    }

    const textWidths = wrappedLines.map((l) => ctx.measureText(l).width);
    const textWidth = Math.max(...textWidths);
    const textHeight = wrappedLines.length * lineHeight;

    const padding = Math.round(fontSize * 0.4);

    const x = img.width - textWidth - padding * 2;
    const y = img.height - textHeight - padding * 2;

    /* background */
    ctx.fillStyle = "rgba(0,0,0,0.45)";
    ctx.fillRect(
      x - padding,
      y - padding,
      textWidth + padding * 2,
      textHeight + padding * 2,
    );

    /* text */
    ctx.fillStyle = "#fff";

    wrappedLines.forEach((line, i) => {
      ctx.fillText(line, x, y + i * lineHeight);
    });

    return canvas.toDataURL("image/jpeg", 0.92);
  }

  /* -----------------------------
       Add EXIF + GPS metadata
    ----------------------------- */
  async function geoTagImage(imageSrc, lat, lng, meta) {
    if (!imageSrc) return;

    const m = 1000000;
    const xlat = lat * m;
    const xlng = lng * m;

    const ts = new Date();
    const tsFormatted = ts.toLocaleString();

    const gpsText = `GPS: ${lat}, ${lng}
      ${tsFormatted}
      Laddar Africa`;

    const watermarkedImg = await addWatermark(imageSrc, gpsText);

    const zeroth = {};
    zeroth[piexif.ImageIFD.Make] = "Laddar.Africa";
    zeroth[piexif.ImageIFD.Model] = "LaddarOS";
    zeroth[piexif.ImageIFD.Software] = "AgentApp_DataForm";

    const gps = {};
    gps[piexif.GPSIFD.GPSLatitudeRef] = lat > 0 ? "N" : "S";
    gps[piexif.GPSIFD.GPSLatitude] = [xlat, m];
    gps[piexif.GPSIFD.GPSLongitudeRef] = lng > 0 ? "E" : "W";
    gps[piexif.GPSIFD.GPSLongitude] = [xlng, m];

    const exif = {};
    exif[piexif.ExifIFD.DateTimeOriginal] = ts;
    exif[piexif.ExifIFD.UserComment] = meta;

    // Combine EXIF data
    const exifObj = { "0th": zeroth, Exif: exif, GPS: gps };
    const exifStr = piexif.dump(exifObj);

    // Insert EXIF into image
    const newImage = piexif.insert(exifStr, watermarkedImg);
    return newImage;
  }

  // async function addWatermark(base64, text) {
  //   const img = await new Promise((resolve, reject) => {
  //     const i = new Image();
  //     i.onload = () => resolve(i);
  //     i.onerror = reject;
  //     i.src = base64;
  //   });

  //   const canvas = document.createElement("canvas");
  //   const ctx = canvas.getContext("2d");

  //   canvas.width = img.width;
  //   canvas.height = img.height;
  //   ctx.drawImage(img, 0, 0);

  //   const fontSize = Math.round(img.width * (isMobile ? 0.05 : 0.03));
  //   const lineHeight = fontSize * 1.3;

  //   ctx.font = `${fontSize}px Arial`;
  //   ctx.textBaseline = "top";

  //   const maxWidth = img.width * 0.9;
  //   const words = text.split(" ");
  //   let line = "";
  //   const lines = [];

  //   words.forEach((word) => {
  //     const testLine = line ? `${line} ${word}` : word;
  //     if (ctx.measureText(testLine).width > maxWidth) {
  //       lines.push(line);
  //       line = word;
  //     } else line = testLine;
  //   });

  //   if (line) lines.push(line);

  //   const padding = Math.round(fontSize * 0.4);
  //   const textHeight = lines.length * lineHeight;
  //   const textWidth = Math.max(...lines.map((l) => ctx.measureText(l).width));

  //   const x = img.width - textWidth - padding * 2;
  //   const y = img.height - textHeight - padding * 2;

  //   ctx.fillStyle = "rgba(0,0,0,0.5)";
  //   ctx.fillRect(
  //     x - padding,
  //     y - padding,
  //     textWidth + padding * 2,
  //     textHeight + padding * 2,
  //   );

  //   ctx.fillStyle = "#fff";
  //   lines.forEach((l, i) => ctx.fillText(l, x, y + i * lineHeight));

  //   return canvas.toDataURL("image/jpeg", 0.92);
  // }

  // async function geoTagImage(imageSrc, lat, lng, meta) {
  //   if (!imageSrc) return;

  //   const ts = new Date().toISOString();
  //   const addr = await getGeocodeData(lat, lng);

  //   const text = `GeoTagged Image\n${
  //     addr || ""
  //   }\n${lat}, ${lng}\n${ts}\nLaddar Africa`;
  //   const watermarked = await addWatermark(imageSrc, text);

  //   const exifObj = {
  //     "0th": {
  //       [piexif.ImageIFD.Make]: "Laddar.Africa",
  //     },
  //     Exif: {
  //       [piexif.ExifIFD.DateTimeOriginal]: ts,
  //       [piexif.ExifIFD.UserComment]: meta,
  //     },
  //     GPS: {
  //       [piexif.GPSIFD.GPSLatitudeRef]: lat > 0 ? "N" : "S",
  //       [piexif.GPSIFD.GPSLongitudeRef]: lng > 0 ? "E" : "W",
  //     },
  //   };

  //   return piexif.insert(piexif.dump(exifObj), watermarked);
  // }

  const capture = useCallback(
    async (gpsCoords) => {
      const imageSrc = webcamRef.current.getScreenshot();
      if (!imageSrc) {
        setErrorMsg("No photo image found.");
        showFieldError(true);
        return;
      }

      if (options?.lat && options?.lng && options?.rad) {
        const dist = getHaversineDistance(
          parseFloat(options.lat),
          parseFloat(options.lng),
          parseFloat(gpsCoords.latitude),
          parseFloat(gpsCoords.longitude),
        );

        if (dist > parseFloat(options.rad)) {
          setErrorMsg("You are not within GPS location radius.");
          showFieldError(true);
          return;
        }
      }

      const tagged = await geoTagImage(
        imageSrc,
        gpsCoords.latitude,
        gpsCoords.longitude,
        "Captured using Laddar Africa Data Forms.",
      );

      if (!tagged) {
        setErrorMsg("Geo-tagging failed.");
        showFieldError(true);
        return;
      }

      setCapturedData(
        JSON.stringify({
          image: tagged,
          gps: gpsCoords,
        }),
      );

      setImgSrc(tagged);
      setOpenWebCamDialog(false);
    },
    [webcamRef],
  );

  const { coords, isGeolocationAvailable, isGeolocationEnabled } =
    useGeolocated({ enableHighAccuracy: true });

  function getGPSCoords() {
    if (!isGeolocationAvailable || !isGeolocationEnabled || !coords)
      return false;
    return coords;
  }

  return (
    <Box>
      <Snackbar open={fieldError}>
        <Alert severity="error">{errorMsg}</Alert>
      </Snackbar>

      <Dialog open={openWebCamDialog}>
        <DialogTitle>Take a picture</DialogTitle>
        <DialogContent>
          <Webcam
            width="100%"
            ref={webcamRef}
            screenshotQuality={1}
            screenshotFormat="image/jpeg"
            videoConstraints={videoConstraints}
            mirrored={cameraUsed}
          />
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenWebCamDialog(false)}>
            <Icon>close</Icon>
          </MDButton>

          <MDButton onClick={() => setCameraUsed(!cameraUsed)}>
            <Icon>cameraswitch</Icon>
          </MDButton>

          <MDButton
            onClick={() => {
              const gps = getGPSCoords();
              if (!gps) {
                setErrorMsg("Enable location permissions.");
                showFieldError(true);
                return;
              }
              capture(gps);
            }}
          >
            <Icon>photo_camera</Icon>
          </MDButton>
        </DialogActions>
      </Dialog>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Typography>{displayName}</Typography>
        </Grid>

        <Grid item xs={6}>
          <Box textAlign="right">
            <IconButton onClick={() => setOpenWebCamDialog(true)}>
              <Icon>camera_alt</Icon>
            </IconButton>
          </Box>
        </Grid>
      </Grid>

      {/* ✅ CAPTURE BOX */}
      <Box
        sx={{
          border: "2px dashed #d2d6da",
          borderRadius: "12px",
          height: 220,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          overflow: "hidden",
          backgroundColor: "#f8f9fa",
          mt: 2,
        }}
      >
        {!imgSrc && (
          <Box textAlign="center">
            <IconButton
              onClick={() => setOpenWebCamDialog(true)}
              sx={{
                width: 64,
                height: 64,
                backgroundColor: "#e3f2fd",
              }}
            >
              <Icon>photo_camera</Icon>
            </IconButton>

            <Typography variant="caption" mt={1} display="block">
              Tap to take photo
            </Typography>
          </Box>
        )}

        {imgSrc && (
          <>
            <img
              src={imgSrc}
              alt="captured"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />

            <IconButton
              onClick={() => {
                setCapturedData(null);
                setImgSrc(null);
              }}
              sx={{
                position: "absolute",
                top: 10,
                right: 10,
                backgroundColor: "white",
              }}
            >
              <Icon color="error">close</Icon>
            </IconButton>
          </>
        )}
      </Box>

      {imgSrc && coords && (
        <Typography mt={1} fontSize={14}>
          GPS: {coords.latitude.toFixed(6)}, {coords.longitude.toFixed(6)}
        </Typography>
      )}

      <input type="hidden" name={fieldRef} value={capturedData} />
    </Box>
  );
}

GeoTaggedImageField.propTypes = {
  fieldRef: PropTypes.string.isRequired,
  displayName: PropTypes.string.isRequired,
  options: PropTypes.array,
};

export default GeoTaggedImageField;
